import { type FormEvent, useState } from 'react'
import { useAuth } from '../../lib/auth-context.js'
import { SUBMIT_ERROR_FALLBACK, toUserMessage } from '../../lib/error-copy.js'
import { LOADING } from '../../lib/labels.js'
import { useToast } from '../../lib/toast.js'
import { SettingsSectionCard, SettingsUnavailableNotice } from './SettingsSectionCard.js'

/** Google だけでログインしている人には出さない（判断は設定画面 `pages/Settings.tsx` で行う） */
export function SettingsPasswordSection() {
  const { session, updatePassword } = useAuth()
  const toast = useToast()

  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!session) {
    return (
      <SettingsSectionCard title="パスワード" description="ログイン用のパスワードを変更します。">
        <SettingsUnavailableNotice message="アカウント登録後に利用できます。" />
      </SettingsSectionCard>
    )
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)

    if (newPassword.length < 8) {
      setError('パスワードは 8 文字以上にしてください')
      return
    }
    if (newPassword !== confirmPassword) {
      setError('パスワードが一致しません。もう一度入力してください')
      return
    }

    setSubmitting(true)
    try {
      await updatePassword(newPassword)
      setNewPassword('')
      setConfirmPassword('')
      toast.success('パスワードを変更しました')
    } catch (err) {
      setError(toUserMessage(err, SUBMIT_ERROR_FALLBACK))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <SettingsSectionCard title="パスワード" description="ログイン用のパスワードを変更します。">
      <form onSubmit={onSubmit} className="space-y-3">
        <div>
          <label htmlFor="settings-new-password" className="block text-sm font-medium mb-1">
            新しいパスワード
          </label>
          <input
            id="settings-new-password"
            type="password"
            autoComplete="new-password"
            minLength={8}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="w-full px-3 py-2 border border-border rounded-md text-sm bg-background"
          />
        </div>
        <div>
          <label htmlFor="settings-confirm-password" className="block text-sm font-medium mb-1">
            新しいパスワード（確認）
          </label>
          <input
            id="settings-confirm-password"
            type="password"
            autoComplete="new-password"
            minLength={8}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="w-full px-3 py-2 border border-border rounded-md text-sm bg-background"
          />
        </div>
        {error && (
          <p className="text-xs text-destructive bg-destructive/5 border border-destructive/30 rounded-md px-3 py-2">
            {error}
          </p>
        )}
        <div className="flex items-center justify-end">
          <button
            type="submit"
            disabled={submitting || !newPassword || !confirmPassword}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-40"
          >
            {submitting ? LOADING.saving : 'パスワードを変更'}
          </button>
        </div>
      </form>
    </SettingsSectionCard>
  )
}
